import React from "react";
import { motion } from "framer-motion";
import { BRAND, HERO_IMAGE } from "@/lib/site";
import { HeartHandshake, ArrowRight } from "lucide-react";

export default function Hero({ onDonate }) {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 lg:pt-32 pb-16 lg:pb-24 bg-brand-cream overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 -right-24 h-[420px] w-[420px] rounded-full bg-brand-forest/5 blur-3xl" />
        <div className="absolute top-40 -left-32 h-[360px] w-[360px] rounded-full bg-brand-gold/10 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <motion.div
            className="lg:col-span-6"
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 border border-brand-border text-xs uppercase tracking-[0.2em] text-brand-forest font-medium">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-gold" />
              A foundation for mothers & families
            </div>

            <h1 className="mt-6 font-serif text-balance text-4xl sm:text-5xl lg:text-[64px] leading-[1.02] text-brand-ink">
              Empowering mothers.{" "}
              <span className="italic text-brand-forest">Strengthening</span>{" "}
              families. Transforming lives.
            </h1>

            <p className="mt-6 text-pretty text-brand-muted text-lg max-w-xl leading-relaxed">
              {BRAND.mission}
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button
                onClick={onDonate}
                data-testid="hero-donate-button"
                className="btn-primary"
              >
                <HeartHandshake className="h-5 w-5" />
                Donate Now
              </button>
              <a
                href="#get-involved"
                data-testid="hero-getinvolved-button"
                className="btn-outline"
              >
                Get Involved
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>

            <div className="mt-10 flex items-center gap-6">
              <div className="flex -space-x-3">
                {[
                  "https://images.pexels.com/photos/6281959/pexels-photo-6281959.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=120&w=120",
                  "https://images.pexels.com/photos/27499880/pexels-photo-27499880.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=120&w=120",
                  "https://images.pexels.com/photos/5386422/pexels-photo-5386422.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=120&w=120",
                ].map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt=""
                    className="h-10 w-10 rounded-full object-cover ring-2 ring-brand-cream"
                  />
                ))}
              </div>
              <p className="text-sm text-brand-muted">
                <span className="font-semibold text-brand-ink">2,400+</span>{" "}
                mothers walking this journey with us.
              </p>
            </div>
          </motion.div>

          <motion.div
            className="lg:col-span-6"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: "easeOut", delay: 0.1 }}
          >
            <div className="relative">
              <div className="relative aspect-[4/5] sm:aspect-[5/6] lg:aspect-[4/5] w-full rounded-[28px] overflow-hidden ring-1 ring-brand-border">
                <img
                  src={HERO_IMAGE}
                  alt="A mother holding her child with warmth and strength"
                  className="absolute inset-0 h-full w-full object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-brand-navy/40 via-transparent to-transparent" />
              </div>

              <div className="hidden md:block absolute -left-6 bottom-8 bg-brand-cream rounded-2xl p-5 max-w-[260px] ring-1 ring-brand-border shadow-[0_18px_40px_-22px_rgba(10,92,54,0.35)]">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-brand-forest/10 flex items-center justify-center">
                    <HeartHandshake className="h-5 w-5 text-brand-forest" />
                  </div>
                  <div>
                    <div className="text-2xl font-serif font-semibold text-brand-ink leading-none">
                      24
                    </div>
                    <div className="text-xs text-brand-muted">
                      Communities served
                    </div>
                  </div>
                </div>
                <div className="stroke-decor mt-3" />
                <p className="text-xs text-brand-muted mt-3 leading-relaxed">
                  From Dar es Salaam to Mwanza, our circles meet weekly — in
                  person and online.
                </p>
              </div>

              <div className="hidden md:block absolute -right-4 top-10 bg-brand-navy text-white rounded-2xl px-5 py-4 ring-1 ring-white/10 shadow-[0_18px_40px_-22px_rgba(26,54,93,0.5)]">
                <div className="text-[11px] uppercase tracking-[0.2em] text-brand-gold/90 font-medium">
                  Our promise
                </div>
                <div className="font-serif text-lg leading-snug mt-1 max-w-[180px]">
                  No mother walks alone.
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
